/**
 * StatusChip — Badge de status padronizado
 *
 * Usado em listas de playlists, displays, itens com estado ativo/inativo.
 * Substitui os <Box component="span"> inline espalhados pelas telas.
 *
 * Uso:
 *   <StatusChip status="active" />
 *   <StatusChip status="online" />
 *   <StatusChip status="offline" />
 *   <StatusChip status="inactive" label="Pausado" />
 */

import { Box, useTheme } from '@mui/material'
import { ds } from '../../theme/ds'

type Status = 'active' | 'inactive' | 'online' | 'offline'

type Props = {
  status: Status
  label?: string
}

const config: Record<Status, { label: string; colorDark: string; colorLight: string; glowDark: string }> = {
  active:   { label: 'Ativo',    colorDark: '#14b8a0', colorLight: '#0d9488', glowDark: '0 0 6px rgba(20,184,160,0.7)' },
  online:   { label: 'Online',   colorDark: '#14b8a0', colorLight: '#0d9488', glowDark: '0 0 6px rgba(20,184,160,0.7)' },
  offline:  { label: 'Offline',  colorDark: '#f87171', colorLight: '#ef4444', glowDark: 'none' },
  inactive: { label: 'Inativo',  colorDark: '#7fa3b0', colorLight: '#8aaabb', glowDark: 'none' },
}

export default function StatusChip({ status, label }: Props) {
  const isDark = useTheme().palette.mode === 'dark'
  const cfg    = config[status]
  const color  = isDark ? cfg.colorDark : cfg.colorLight
  const isPos  = status === 'active' || status === 'online'

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        bgcolor: isPos
          ? (isDark ? 'rgba(20,184,160,0.15)' : 'rgba(13,148,136,0.1)')
          : (status === 'offline' ? 'rgba(248,113,113,0.1)' : 'rgba(127,163,176,0.1)'),
        color,
        fontSize: ds.font.badge,
        fontWeight: ds.font.semibold,
        px: '9px',
        py: '3px',
        borderRadius: `${ds.radius.pill}px`,
        border: `1px solid ${color}33`,
        lineHeight: 1.6,
        '&::before': {
          content: '""',
          width: 5,
          height: 5,
          borderRadius: '50%',
          bgcolor: color,
          flexShrink: 0,
          boxShadow: isPos ? cfg.glowDark : 'none',
        },
      }}
    >
      {label ?? cfg.label}
    </Box>
  )
}
